// ── my-cohorts-collection: useCollection client adapter for /my-cohorts ───────

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  humanizeLabel,
  useCollection,
  type CollectionColumnDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingCohortDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import { cohortStatusTone, orDash } from "./cohort-detail-helpers";

export const MY_COHORTS_PAGE_SIZE = 25;
export const MY_COHORTS_DEFAULT_SORT = { id: "name", dir: "asc" as const };

export type MyCohortRow = CoachingCohortDto & Record<string, unknown>;

export interface MyCohortsCollectionOptions {
  linkToDetail: boolean;
}

export function buildMyCohortsCollectionColumns(options: MyCohortsCollectionOptions): CollectionColumnDef<MyCohortRow>[] {
  return [
    {
      id: "name",
      header: "Cohort",
      sortable: true,
      sortAccessor: (row) => row.name ?? "",
      searchValue: (row) =>
        `${row.name ?? ""} ${row.company_name ?? ""} ${row.program_name ?? ""} ${row.coach_name ?? ""}`,
      priority: 1,
      cell: (row) =>
        options.linkToDetail ? (
          <Link
            to={`/cohorts/${encodeURIComponent(row.id)}`}
            className="font-medium text-link no-underline hover:underline"
            data-exact-set-name="Open cohort">
            {row.name || "Unnamed cohort"}
          </Link>
        ) : (
          <span className="font-medium text-strong">{row.name || "Unnamed cohort"}</span>
        )
    },
    {
      id: "company_name",
      header: "Company",
      sortable: true,
      sortAccessor: (row) => row.company_name ?? "",
      priority: 2,
      cell: (row) => <span className="text-ink-muted">{orDash(row.company_name)}</span>
    },
    {
      id: "program_name",
      header: "Program",
      sortable: true,
      sortAccessor: (row) => row.program_name ?? "",
      priority: 2,
      cell: (row) => <span className="text-ink-muted">{orDash(row.program_name)}</span>
    },
    {
      id: "coach_name",
      header: "Coach",
      sortable: true,
      sortAccessor: (row) => row.coach_name ?? "",
      priority: 3,
      cell: (row) => <span className="text-ink-muted">{orDash(row.coach_name)}</span>
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status ?? "active",
      priority: 2,
      cell: (row) => <Badge variant={cohortStatusTone(row.status)}>{humanizeLabel(row.status ?? "active")}</Badge>
    }
  ];
}

export function useMyCohortsCollection(
  rows: CoachingCohortDto[],
  options: MyCohortsCollectionOptions,
  routePath?: string
): UseCollectionResult<MyCohortRow> {
  const columns = useMemo(() => buildMyCohortsCollectionColumns(options), [options]);
  const initialState = useMemo(
    () => ({ sort: MY_COHORTS_DEFAULT_SORT, ...readDirectoryInitialState(routePath, MY_COHORTS_PAGE_SIZE) }),
    [routePath]
  );

  return useCollection<MyCohortRow>({
    mode: "client",
    rows: rows as MyCohortRow[],
    columns,
    pageSize: MY_COHORTS_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-my-cohorts",
    initialState,
    getRowKey: (row) => row.id
  });
}
